import { QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Toaster } from 'sonner'

import { AuthProvider } from '@/features/auth/auth-context'
import { useSession } from '@/features/auth/use-session'
import { I18nProvider } from '@/shared/i18n'
import { useApplyUiPreferences } from '@/shared/hooks/use-apply-ui-preferences'
import { useUiStore } from '@/shared/stores/ui-store'

import { ErrorBoundary } from './routes/ErrorBoundary'
import { createQueryClient } from './query-client'

/**
 * El idioma sale del PERFIL, no del navegador.
 *
 * `GET /auth/me` (o `GET /portal/me` para el contacto de cliente) trae
 * `locale`; mientras la sesión está en vuelo se dibuja en el idioma por
 * defecto, que es lo mismo que ve un anónimo en el login. Cambiarlo en el
 * perfil invalida la query y toda la app se redibuja en el idioma nuevo sin
 * recargar.
 *
 * Deliberadamente NO se mira `navigator.language`: la promesa es "si elegiste
 * portugués lo ves en portugués desde cualquier máquina", y el navegador de
 * una máquina prestada no sabe nada de esa elección.
 */
function I18nFromSession({ children }: { children: ReactNode }) {
  const { me } = useSession()
  return <I18nProvider locale={me?.locale}>{children}</I18nProvider>
}

function UiPreferences({ children }: { children: ReactNode }) {
  useApplyUiPreferences()
  return <>{children}</>
}

function AppToaster() {
  const theme = useUiStore((state) => state.theme)

  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      duration={4000}
      toastOptions={{
        style: {
          background: 'hsl(var(--surface-1))',
          color: 'hsl(var(--text-1))',
          border: '1px solid hsl(var(--hairline))',
          borderRadius: 'var(--radius-card)',
          fontSize: '13px',
          // Sin sombras difusas en ningun lado, tampoco en los toasts.
          boxShadow: 'none',
        },
      }}
    />
  )
}

export function AppProviders({ children }: { children: ReactNode }) {
  // El QueryClient se crea una sola vez por montaje de la app, en estado y no
  // en un modulo: un singleton de modulo se comparte entre tests.
  const [queryClient] = useState(createQueryClient)

  return (
    <ErrorBoundary boundary="app">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <I18nFromSession>
            <UiPreferences>
              {children}
              <AppToaster />
            </UiPreferences>
          </I18nFromSession>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
